/**
 * 13 - Blow the Bridge (10 §2): the road bridge over the river in the belt stands on 2-3 piers, which stand
 * IN the water under its deck and open to a time bomb only. Plant on each of them - a charge laid in
 * mid-stream can drop two, as one bomb can straddle two aircraft in mission 3. The bridge guard's camp is the
 * compound on the north bank; its picket holds both ends of the span.
 *
 * A convoy comes down the road and over the bridge at a stated time - a bonus clock, never a loss. It is real
 * (ROUND4 §1): lorries with men aboard drive the road from the camp's gate, over the span, and off the south
 * end of their run, while a foot escort walks the same road ahead of them. Blow the piers while the lorries
 * are on the deck and the span takes them into the river: the Silver Star deed of 10 §2.
 */

import { PROPS } from '../../sim/props';
import { HAZARDS } from '../../sim/tuning';
import { checker, clutter, guard, guardAtDoor, guardsAbout, structure } from './helpers';
import { file, lamp, squad } from './helpers3a';
import { checkAfloat, checkRuleText, checkTargets, inWater, timedRules, waiting } from './helpers3aAsserts';
import { CAREFUL, measureRoute, retime, round5 } from './helpers3aRoute';
import { reachAt, roadBridge, underDeck, widen } from './helpers3aWater';
import { roadRun, runPasses, vehicle, vehiclesOf } from './helpers4Vehicles';
import type { ArchetypeSpec } from './types';

export const piersFor = (threat: number): number => (threat >= 5 ? 3 : 2);
export const escortFor = (threat: number): number => (threat >= 7 ? 4 : 3);
/** Lorries in the convoy, and the section riding in each of them. */
export const lorriesFor = (threat: number): number => (threat >= 6 ? 3 : 2);
export const LOAD = 3;
/** The run ends this far south of the far bank: down the road, out of the camp's world. */
const RUN_OUT = 34;

/** Three piers need this much river: 1.8 m of water between them and 0.6 m to either bank. */
const WIDE = 12;
/**
 * Pier centres stand this far off the deck's centre line, all on one side: under the deck's EDGE (the deck is the
 * crossing lane and 20 cm more - render/kit/pipeline.ts BRIDGE_HALF_WIDTH), so half of each pier is under the
 * planking and half shows beside the rail as a cutwater. The rest of the lane - 4 m of it - stays walkable.
 */
export const PIER_OFFSET = HAZARDS.crossingHalfWidth + 0.2;
/** What is left of the crossing lane beside a pier's collider: a man, and the escort in file, must get past. */
export const CLEAR_LANE = 3.8;
const TAG = 'convoyCrosses';

export const blowBridge: ArchetypeSpec = {
  id: 'blow-bridge',
  name: 'BLOW THE BRIDGE',
  nativeBiome: 'temperate',
  baseThreat: 4,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  // Wide: the road never has to bend to find the gate (it runs straight over the bridge), and the quiet ford
  // comes out by the camp's corner, as it does for the radar array - a short crawl in the open, not a long one.
  size: { w: [54, 62], d: [30, 34] },
  river: true,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const roadX = g.ctx.plan.lines[g.ctx.plan.lines.length - 1]?.roadX ?? g.ctx.plan.roadEnd.x;
    const count = piersFor(g.threat);
    const found = reachAt(g, roadX);
    const ids: number[] = [];
    if (found) {
      if (count > 2) widen(g, found.barrier, WIDE);
      const reach = reachAt(g, roadX) ?? found;
      const bridge = roadBridge(g, reach);
      const width = reach.barrier.course.width;
      const spacing = (width - 1.2 - PROPS.bridgePier.shapes.length * 2.4) / (count - 1);
      const side = g.rng.chance(0.5) ? 1 : -1;
      for (let i = 0; i < count; i++) {
        // No room is asked for: the crossing is kept clear of everything else, and a pier belongs in it.
        const z = Math.round((reach.z + (i - (count - 1) / 2) * Math.min(7, Math.max(3.4, spacing))) * 2) / 2;
        ids.push(g.map.add('bridgePier', bridge.x + side * PIER_OFFSET, z, { target: true }).id);
      }
      // The picket: both ends of the span, and a lamp on the home bank looking down it.
      guard(g, roadX - side * 1.5, reach.north - 3, 0);
      guard(g, roadX - side * 3, reach.south + 4, 0);
      if (g.threat >= 3) guard(g, roadX + side * 4, reach.north - 5, 0);
      lamp(g, roadX - side * 6, reach.north - 4);
      // The escort forms up inside the camp's gate, walks the road over the span and a little beyond, and back.
      const gate = g.ctx.plan.roadEnd;
      const beatOf = [
        { x: gate.x, z: gate.z - 5 }, { x: roadX - side, z: reach.north - 3 }, { x: roadX - side, z: reach.south + 3 },
        { x: roadX, z: reach.south + 14 },
      ];
      file(g, squad(g, escortFor(g.threat)), beatOf, TAG);
      // R4: the convoy itself. Lorries come out of the camp gate, cross the deck and drive off the end of
      // their run; the men inside never touch the map unless the ranger stops one.
      const road = (g.map.roads[0]?.points ?? []).map(([x, z]) => ({ x, z }));
      const run = roadRun(road, gate.z - 4, Math.min(g.ctx.sizeZ - 6, reach.south + RUN_OUT));
      for (let i = 0; i < lorriesFor(g.threat); i++) {
        vehicle(g, { kind: 'truck', route: run, wakeTag: TAG, carries: LOAD, speed: 5, back: 9 * i });
      }
    }

    const quarters = structure(g, g.kit.shelter, { margin: 1.2 });
    structure(g, 'hut', { margin: 1.2 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);
    if (quarters) guardAtDoor(g, quarters);
    guardsAbout(g, 1 + g.rng.int(2));
    return {
      objective: {
        kind: 'destroyTargets', label: 'BLOW THE BRIDGE PIERS', targetPropIds: ids, minFraction: 1,
        rules: [
          {
            type: 'timedEvent', atSeconds: 300, effect: 'convoyCrosses', wakes: TAG,
            orders: 'A CONVOY IS DUE OVER THE BRIDGE ON THE CLOCK: ITS ESCORT WALKS THE ROAD',
            warning: 'CONVOY AT THE BRIDGE IN',
          },
          { type: 'recall', mode: 'normal' },
        ],
      },
      // The escort is on the span about when a careful man gets to the river (helpers3aRoute.ts).
      finish(def) {
        const route = measureRoute(def);
        if (route) retime(def, (r) => r.effect === 'convoyCrosses', round5(route.toFirst * CAREFUL));
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('blow-bridge');
    const piers = checkTargets(check, def, ['bridgePier']);
    check(
      piers.length === piersFor(def.threat),
      `needs ${piersFor(def.threat)} piers at threat ${def.threat}, has ${piers.length}`,
    );
    check(def.objective.kind === 'destroyTargets' && def.objective.minFraction === 1, 'every pier must be blown');
    check(PROPS.bridgePier.durability === 'bombOnly', 'a pier opens to a time bomb only');
    const river = def.waterBarriers[0];
    check(river !== undefined, 'needs a river');
    const bridges = def.props.filter((p) => p.kind === 'bridge');
    const bridge = bridges.find((b) => piers.every((p) => underDeck(b, p)));
    check(bridge !== undefined, 'every pier must stand under the deck of the road bridge');
    if (bridge && river) {
      check(
        river.crossings.some((c) => Math.abs(c - bridge.x) < 0.01),
        'the bridge must stand on a crossing of the river',
      );
      check((bridge.length ?? 0) >= river.course.width, 'the deck must span the river from bank to bank');
    }
    for (const p of piers) {
      checkAfloat(check, def, p);
      check(probe.breach(p.id) === 0, `pier ${p.id} cannot be walked to`);
      check(
        piers.every((o) => o.id === p.id || Math.abs(o.z - p.z) >= 3.4),
        `pier ${p.id} stands against its neighbour`,
      );
    }
    check(
      piers.every((p) => p.x === piers[0]?.x),
      'the piers stand in one line, leaving the far side of the deck open',
    );
    if (bridge) {
      check(
        piers.every((p) => Math.abs(Math.abs(p.x - bridge.x) - PIER_OFFSET) < 0.01),
        'a pier stands under the edge of the deck: half under the planking, half showing beside it',
      );
      const hx = PROPS.bridgePier.shapes[0]?.hx ?? 0;
      check(
        HAZARDS.crossingHalfWidth + PIER_OFFSET - hx >= CLEAR_LANE,
        `the piers leave less than ${CLEAR_LANE} m of the crossing to walk on`,
      );
    }

    // The convoy: a bonus clock with its men waiting, whose beat crosses the span (the hook for the deed).
    const [convoy] = timedRules(def, 'convoyCrosses');
    check(convoy !== undefined && convoy.failIfIncomplete !== true, 'the convoy is a bonus, never a loss');
    const tag = convoy?.wakes ?? TAG;
    const escort = waiting(def, tag).filter((s) => s.route === undefined);
    check(escort.length === escortFor(def.threat), `the escort numbers ${escortFor(def.threat)}, not ${escort.length}`);
    // R4: the convoy is lorries, they cross the deck, they carry men, and they leave rather than loop for ever.
    const lorries = vehiclesOf(waiting(def, tag));
    check(lorries.length === lorriesFor(def.threat), `the convoy needs ${lorriesFor(def.threat)} lorries`);
    for (const lorry of lorries) {
      check(lorry.kind === 'truck' && lorry.carries === LOAD, `a lorry of the convoy carries ${LOAD} men`);
      check(lorry.route?.loop === false, 'the convoy must drive off the map, not loop its run for ever');
      check(bridge !== undefined && runPasses(lorry, bridge.x, bridge.z, 3), 'the convoy must cross the span');
      const last = lorry.route?.points.points[(lorry.route?.points.points.length ?? 1) - 1];
      check(last !== undefined && last[1] > (bridge?.z ?? 0), 'the convoy must drive away south, past the bridge');
    }
    for (const man of escort) {
      const zs = (man.patrol ?? []).map((n) => n[1]);
      const xs = (man.patrol ?? []).filter((n) => inWater(def, n[0], n[1]));
      check(xs.length === 0, 'an escort man is told to stand in the river');
      check(
        bridge !== undefined && Math.min(...zs, 9999) < bridge.z && Math.max(...zs, -1) > bridge.z,
        'the escort must cross the bridge',
      );
    }
    if (convoy) {
      check(convoy.atSeconds >= 60 && convoy.atSeconds < def.clockSeconds, `the convoy comes at ${convoy.atSeconds} s`);
    }
    checkRuleText(check, def);
    return out;
  },
};
